/**
 * ByDoctorCalendar — Block
 *
 * Time-row × doctor-column grid. Each visible doctor gets a column;
 * each hour of the working day gets a row. Appointments slot into the
 * doctor column at their start time with a height proportional to
 * duration.
 *
 * Flutter parity: `calendar_by_doctor_widget.dart` — the practice
 * management calendar's doctor-board view, used for hospitals /
 * larger clinics where receptionists need to see every doctor's load
 * at a glance.
 *
 * Data contract:
 *   - `doctors`  — column set (typically already filtered by the
 *                  ProviderStrip selection upstream).
 *   - `rowsByDoctor` — `doctorId -> DayListRow[]` (DayListRow ships
 *                  startISO / endISO / patient + status + profile color
 *                  already; the same shape the Day-list calendar
 *                  consumes, so the upstream mapper is reused).
 *   - `hours`    — hour gutter to render (8-17 by default). Practice
 *                  working hours flow through from the page.
 *
 * Empty / loading:
 *   - When `doctors.length === 0` we render a friendly message rather
 *     than an empty grid that looks broken.
 *   - Loading state is upstream's responsibility (parent renders a
 *     spinner before the providers list resolves); when this block is
 *     mounted it assumes providers are known.
 *
 * Intentional React divergence from Flutter (Samer review #86 §5):
 *   Flutter's By-Doctor view renders columns for every provider in
 *   `availableProviders` unconditionally; provider narrowing happens
 *   inside the view via a separate dropdown. React reuses the existing
 *   ProviderStrip checkbox state as the column filter — when the user
 *   has providers checked elsewhere in the calendar, only those
 *   columns render in By-Doctor. Empty checkbox set falls through to
 *   "all providers" so first-visit users see Flutter-parity behaviour.
 *   This unifies the filter UX across Day / Week / Doctor views (one
 *   selection, three views) at the cost of strict Flutter parity. If
 *   parity needs to be restored, pass an explicit `doctors` array
 *   from the parent instead of using `selectedProviderIds`.
 */

import { useMemo, useRef } from 'react';
import { locationTileBg, locationEdgeColor } from '@/scheduling/lib/location-colors';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  type DragEndEvent,
} from '@dnd-kit/core';
import { Ban, Repeat } from 'lucide-react';
import { TimeLabel } from '@/components/atoms';
import { PatientAvatar } from '@/shared/ui/components';
import { StatusBadge, type AppointmentStatusType } from '@/components/atoms';
import { BLOCK_STRIPES } from '@/components/cards/appointment-tile';
import { cn } from '@/lib/utils';
import type { DayListRow } from '@/scheduling/lib/calendar-mapping';
import type { ProviderSummary } from '@/components/blocks/provider-strip';
import { computeOverlapLayout, slotToPercents } from '@/scheduling/lib/overlap-layout';
import {
  quickCreateStartISO,
  dragDeltaToNewStartISO,
} from '@/scheduling/lib/quick-create-slot';
import { CurrentTimeLine } from '@/components/blocks/current-time-line';
import { GutterClock } from '@/components/blocks/gutter-clock';
import { useScrollToNow } from '@/components/blocks/use-scroll-to-now';
import { ColumnsCalendarSkeleton } from '@/components/blocks/calendar-skeleton';
import { AppointmentTileMeta } from '@/components/blocks/appointment-tile-meta';

/** Minute-of-day range for a DayListRow, for overlap layout (PROD-982). */
function rowMinutes(row: DayListRow): { startMin: number; endMin: number } {
  const s = new Date(row.startISO);
  const e = new Date(row.endISO);
  return {
    startMin: s.getHours() * 60 + s.getMinutes(),
    endMin: e.getHours() * 60 + e.getMinutes(),
  };
}

const HOUR_HEIGHT = 60; // px per hour row — narrower than Day view since
                        // columns are wider and we want more on screen.

interface ByDoctorCalendarProps {
  doctors: ProviderSummary[];
  /** Map of doctorId → ordered list of appointments for that doctor. */
  rowsByDoctor: Map<string, DayListRow[]>;
  /** Hour gutter (inclusive) — defaults to 8..17 if not supplied. */
  hours?: number[];
  /** Currently selected day, for the column header strip. */
  selectedDate: Date;
  onAppointmentClick?: (appointmentId: string) => void;
  /**
   * Empty-cell quick-create (PROD-983). Double-click an empty cell →
   * `(doctorId, startISO)` where `startISO` is computed from the click's
   * Y offset within the hour row. The consumer picks the provider's
   * preferred-or-first profile, sets the slot end, and opens the create
   * panel pre-filled. Flutter parity: `calendar_by_doctor_widget.dart:499-530`
   * (double-tap → `onTapNewAppointmentRequest`). By-Doctor only — Week/Room
   * have no equivalent in Flutter.
   */
  onSlotDoubleClick?: (doctorId: string, startISO: string) => void;
  /**
   * Drag-to-reschedule (PROD-984). Dragging a tile vertically fires
   * `(id, newStartISO)` where `newStartISO` is the source start shifted by
   * the drag delta (5-min snap), preserving the appointment's provider
   * (time-only — Flutter `updateEventTimeOnDrag` does NOT reassign). The
   * consumer confirms + fires the reschedule. Flutter parity:
   * `calendar_by_doctor_widget.dart:103,537-628,889-938`.
   */
  onAppointmentDrop?: (payload: { id: string; newStartISO: string }) => void;
  /** The practitioners haven't arrived yet: draw the grid's frame with grey bars, not the empty state. */
  isLoading?: boolean;
}

export function ByDoctorCalendar({
  doctors,
  rowsByDoctor,
  hours = defaultHours(),
  selectedDate,
  onAppointmentClick,
  onSlotDoubleClick,
  onAppointmentDrop,
  isLoading = false,
}: ByDoctorCalendarProps) {
  // 6px activation distance so a normal click/double-click on a tile isn't
  // hijacked as a drag (same as WeekCalendar).
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  // Flat id → row lookup for the drag handler.
  const rowById = useMemo(() => {
    const m = new Map<string, DayListRow>();
    for (const rows of rowsByDoctor.values()) {
      for (const r of rows) m.set(r.id, r);
    }
    return m;
  }, [rowsByDoctor]);

  // Offset-based new start (Flutter `newStartDateAfterDrag`): the vertical
  // drag delta maps pixels → minutes against HOUR_HEIGHT, snapped to 5 min.
  // Horizontal movement is ignored — reschedule is time-only, the provider
  // never changes (review-locked 2026-06-03). Past-time drops are rejected
  // here; working-hours validation is deferred to the reschedule mutation
  // (same documented divergence as PROD-983).
  const handleDragEnd = (event: DragEndEvent) => {
    if (!onAppointmentDrop) return;
    const id = String(event.active.id);
    const row = rowById.get(id);
    if (!row?.startISO) return;
    const newStartISO = dragDeltaToNewStartISO(row.startISO, event.delta.y, HOUR_HEIGHT);
    if (!newStartISO) return; // zero-minute shift → no-op
    if (new Date(newStartISO).getTime() < Date.now()) return; // no past reschedule
    onAppointmentDrop({ id, newStartISO });
  };
  const startHour = hours[0] ?? 8;
  const endHour = (hours[hours.length - 1] ?? 17) + 1;

  // Scroll to current hour on mount. Hooks stay above the early returns: below them the view crashed once the
  // columns loaded ("Rendered more hooks than during the previous render", 15 Oct). Before the grid mounts the ref is
  // empty, so nothing latches until it does.
  const docScrollRef = useRef<HTMLDivElement>(null);
  useScrollToNow(docScrollRef, { startHour, endHour, hourHeight: HOUR_HEIGHT, key: selectedDate.toDateString() });

  if (isLoading && doctors.length === 0) {
    const label = selectedDate.toLocaleDateString('en-US', { weekday: 'short', day: '2-digit', month: 'short' });
    return <ColumnsCalendarSkeleton label={`By doctor · ${label}`} hours={hours} hourHeight={HOUR_HEIGHT} />;
  }
  if (doctors.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-text-muted">
        No doctors selected — pick a provider from the strip above.
      </div>
    );
  }

  // bottom of last row
  // Current-time line (PROD-981) shows only when the displayed day is today.
  const isShowingToday = selectedDate.toDateString() === new Date().toDateString();
  const dateLabel = selectedDate.toLocaleDateString('en-US', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  });

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
    <div className="flex flex-col flex-1 min-h-0 bg-surface-card">
      <div className="px-4 py-3 border-b border-border-default">
        <p className="text-[length:var(--type-label-size)] font-medium leading-[var(--type-label-line)] text-text-secondary">
          By doctor · {dateLabel}
        </p>
      </div>

      <div ref={docScrollRef} data-cal-scroll className="scrollbar-hide">
       <div data-cal-sheet>
        {/* Doctor column header strip — pinned top, its clock corner pinned left too (showcase-bridge.css). */}
        <div data-cal-head className="flex h-10 items-center border-b border-border-default">
          {/* PROD-1797 — see by-room-calendar: the gutter header was empty while
              Day showed the live clock + GMT offset. Client-reported. */}
          <GutterClock data-cal-gutter className="w-16 flex-shrink-0 border-r border-border-subtle px-2 py-1" />
          {doctors.map((d) => (
            <div
              key={d.id}
              className="flex-1 min-w-[160px] border-r border-border-subtle last:border-r-0 px-3 py-2"
            >
              <div className="flex items-center gap-2 min-w-0">
                <PatientAvatar
                  name={d.name}
                  photoUrl={d.photo}
                  size="sm"
                  variant="patient"
                />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-text-primary truncate">
                    {d.name}
                  </p>
                  <p className="text-xs text-text-muted truncate">
                    {d.specialty}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Time grid */}
        <div className="relative">
          {hours.map((hour) => (
            <div
              key={hour}
              className="flex border-b border-border-subtle"
              style={{ height: HOUR_HEIGHT }}
            >
              <div data-cal-gutter className="w-16 flex-shrink-0 px-2 pt-1 border-r border-border-subtle">
                <TimeLabel hour={hour} />
              </div>
              {doctors.map((d) => (
                <div
                  key={d.id}
                  className={`flex-1 min-w-[160px] border-r border-border-subtle last:border-r-0${
                    onSlotDoubleClick ? ' cursor-pointer hover:bg-blue-100/20' : ''
                  }`}
                  onDoubleClick={
                    onSlotDoubleClick
                      ? (e) => {
                          // Click Y within the cell → fraction of the hour →
                          // start time (PROD-983). The appointment tiles sit
                          // in a pointer-events-auto overlay, so this only
                          // fires on genuinely empty cell area. Defensive
                          // guard (review §3): only act on the cell background
                          // itself, never a bubbled child, so a future child
                          // element can't accidentally trigger quick-create.
                          if (e.target !== e.currentTarget) return;
                          const rect = e.currentTarget.getBoundingClientRect();
                          const fraction =
                            rect.height > 0
                              ? (e.clientY - rect.top) / rect.height
                              : 0;
                          onSlotDoubleClick(
                            d.id,
                            quickCreateStartISO(selectedDate, hour, fraction),
                          );
                        }
                      : undefined
                  }
                />
              ))}
            </div>
          ))}

          {/* Absolute-positioned appointment tiles, layered above the
              grid. We compute column index from the doctors array (since
              the grid uses flex-1, columns share width equally). The
              tile uses left:calc(64px + index * columnWidth) — but flex-1
              widths aren't known at compute time, so we use a grid
              template overlay instead: percentage left + width. */}
          <div
            className="absolute inset-0 pl-16 grid pointer-events-none"
            style={{
              gridTemplateColumns: `repeat(${doctors.length}, minmax(160px, 1fr))`,
            }}
          >
            {doctors.map((d) => {
              const rows = rowsByDoctor.get(d.id) ?? [];
              // Side-by-side layout for overlapping appointments in this
              // doctor column (PROD-982).
              const items = rows.map((row) => ({ row, ...rowMinutes(row) }));
              const layout = computeOverlapLayout(items);
              return (
                <div key={d.id} className="relative">
                  {items.map((it) => {
                    const slot = layout.get(it);
                    const { leftPct, widthPct } =
                        slot && slot.columnCount > 1
                          ? slotToPercents(slot)
                      : { leftPct: undefined, widthPct: undefined };
                    return (
                      <DraggableApptTile
                        key={it.row.id}
                        row={it.row}
                        startHour={startHour}
                        endHour={endHour}
                        leftPct={leftPct}
                        widthPct={widthPct}
                        onClick={onAppointmentClick}
                        draggable={!!onAppointmentDrop}
                      />
                    );
                  })}
                </div>
              );
            })}
          </div>
          {/* Current-time "now" line (PROD-981) — spans all doctor columns,
              offset past the time gutter; today only. */}
          {isShowingToday ? (
            <CurrentTimeLine
              startHour={startHour}
              endHour={endHour}
              hourHeight={HOUR_HEIGHT}
              className="left-16 right-0"
            />
          ) : null}
        </div>
       </div>
      </div>
    </div>
    </DndContext>
  );
}

interface AppointmentTileSimpleProps {
  row: DayListRow;
  startHour: number;
  endHour: number;
  /** Overlap geometry (PROD-982) — side-by-side placement within the column. */
  leftPct?: number;
  widthPct?: number;
  onClick?: (id: string) => void;
  /** dnd-kit drag wiring (PROD-984), threaded from DraggableApptTile. */
  dragRef?: (el: HTMLElement | null) => void;
  dragListeners?: ReturnType<typeof useDraggable>['listeners'];
  dragAttributes?: ReturnType<typeof useDraggable>['attributes'];
  dragTransform?: { x: number; y: number } | null;
}

/**
 * Per-tile drag wrapper (PROD-984). `useDraggable` must be a component-level
 * hook, so each tile gets its own instance here; the drag wiring is threaded
 * into AppointmentTileSimple's positioned button (transform composes with the
 * tile's absolute top/height). `draggable=false` renders a plain, click-only
 * tile (no listeners).
 */
function DraggableApptTile({
  draggable,
  ...rest
}: AppointmentTileSimpleProps & { draggable: boolean }) {
  const { setNodeRef, listeners, attributes, transform } = useDraggable({
    id: rest.row.id,
    disabled: !draggable,
  });
  return (
    <AppointmentTileSimple
      {...rest}
      dragRef={draggable ? setNodeRef : undefined}
      dragListeners={draggable ? listeners : undefined}
      dragAttributes={draggable ? attributes : undefined}
      dragTransform={transform}
    />
  );
}

function AppointmentTileSimple({
  row,
  startHour,
  endHour,
  leftPct,
  widthPct,
  onClick,
  dragRef,
  dragListeners,
  dragAttributes,
  dragTransform,
}: AppointmentTileSimpleProps) {
  // Drag transform composes with the tile's absolute top/height; 0.5 opacity
  // + z-30 while dragging so the moving tile reads above its neighbours.
  // VERTICAL-LOCKED (review §3): the X translate is pinned to 0 so the tile
  // can only move up/down — it can never appear to cross into another
  // doctor's column. Reschedule is time-only (provider never changes), and
  // this makes that visually unambiguous (no cross-column reassignment
  // expectation). The drop math already ignores `delta.x`.
  const dragStyle = dragTransform
    ? {
        transform: `translate3d(0px, ${dragTransform.y}px, 0)`,
        opacity: 0.5,
        zIndex: 30,
      }
    : undefined;
  // Overlap geometry (PROD-982). `!= null` so leftPct === 0 positions at
  // `left: 0%` (Samer review §4/§5). Small FIXED 2px gutter on the right —
  // capped so it stays negligible on narrow columns at high column counts
  // (Samer review §2). Spans the column when not provided.
  const isOverlap = leftPct != null && widthPct != null;
  const posClass = isOverlap ? 'absolute' : 'absolute left-1 right-1';
  const overlapStyle = isOverlap
    ? { left: `${leftPct}%`, width: `calc(${widthPct}% - 2px)` }
    : null;
  const start = new Date(row.startISO);
  const end = new Date(row.endISO);
  const startMinutes = start.getHours() * 60 + start.getMinutes();
  const endMinutes = end.getHours() * 60 + end.getMinutes();
  const gridStartMinutes = startHour * 60;
  const gridEndMinutes = endHour * 60;

  // Clip to the visible window so an early-morning or late-night
  // appointment doesn't overflow into the next column.
  const top =
    ((Math.max(startMinutes, gridStartMinutes) - gridStartMinutes) / 60) *
    HOUR_HEIGHT;
  // Min-height floor — 28px keeps a 15-min appointment (which would
  // otherwise render at 15px on the 60px/hour gutter) tall enough to
  // remain a comfortable click target while still showing the patient
  // name + status badge legibly. Bumped from 20px per Samer review
  // #86 §3 (15-min appointments shouldn't collapse to unclickable).
  const height = Math.max(
    ((Math.min(endMinutes, gridEndMinutes) -
      Math.max(startMinutes, gridStartMinutes)) /
      60) *
      HOUR_HEIGHT,
    28,
  );

  if (endMinutes <= gridStartMinutes || startMinutes >= gridEndMinutes) {
    return null;
  }

  // Block Availability entry — striped grey tile with the reason + time,
  // not a patient/status layout. Flutter parity: calendar_event_card_widget.
  if (row.isBlock) {
    const label = row.note?.trim() || 'Blocked';
    return (
      <button
        type="button"
        ref={dragRef}
        onClick={() => onClick?.(row.id)}
        className={cn(
          posClass,
          'rounded-md border border-border-default text-left px-2 py-1.5 shadow-sm hover:shadow-md transition-shadow pointer-events-auto overflow-hidden flex flex-col gap-1',
          dragRef ? 'cursor-grab active:cursor-grabbing touch-none' : '',
          BLOCK_STRIPES,
        )}
        style={{ top, height, ...overlapStyle, ...dragStyle }}
        // PROD-794: block "reason" is staff-typed free text that can carry PHI
        // (e.g. a patient name). Keep it out of the native `title` (tooltip /
        // a11y tree) — the visible tile body below still shows it. Mirrors the
        // appointment-tile fix and the PROD-560 pattern on ByRoomCalendar.
        title={`Blocked — ${row.timeRange}`}
        {...dragListeners}
        {...dragAttributes}
      >
        <div className="flex items-center gap-1 min-w-0">
          {row.isRecurring ? (
            <Repeat className="h-3 w-3 text-text-muted shrink-0" aria-label="Recurring block" />
          ) : (
            <Ban className="h-3 w-3 text-text-muted shrink-0" />
          )}
          <span className="text-xs font-semibold text-text-secondary truncate">
            {label}
          </span>
        </div>
        <span className="text-xs text-text-muted truncate">{row.timeRange}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      ref={dragRef}
      onClick={() => onClick?.(row.id)}
      className={cn(
        posClass,
        'rounded-md border text-left pl-3 pr-2 py-1.5 shadow-sm hover:shadow-md transition-shadow pointer-events-auto overflow-hidden',
        'flex flex-col gap-1',
        dragRef ? 'cursor-grab active:cursor-grabbing touch-none' : '',
      )}
      style={{
        top: top + 4,
        height: Math.max(height - 8, 20),
        ...overlapStyle,
        ...dragStyle,
        backgroundColor: locationTileBg(row.profileLocation),
      }}
      // PHI-FREE — only the time range + status. Patient + doctor names
      // remain in the visible tile body where the staff-only viewport
      // permission already gates exposure.
      title={`${row.timeRange} — ${row.statusDisplay}`}
      {...dragListeners}
      {...dragAttributes}
    >
      {/* The category edge is the card's whole left shoulder, a 4 px colour bar clipped by the card's corners, not a left border bent by them (Antonin, 13 Oct). */}
      <span aria-hidden data-event-edge className="absolute inset-y-0 left-0 w-1" style={{ background: locationEdgeColor(row.profileLocation, row.profileColor) }} />
      <div className="flex items-center gap-1 min-w-0">
        <span className="text-xs font-semibold text-text-primary truncate flex-1 min-w-0">
          {row.patientName}
        </span>
        {/* Importance badge + location icon (PROD-990). */}
        <AppointmentTileMeta
          location={row.profileLocation}
          locationName={row.locationName}
          profileColor={row.profileColor}
          importance={row.patientImportance}
          importanceDisplay={row.patientImportanceDisplay}
        />
      </div>
      <div className="flex items-center gap-1 min-w-0">
        <span className="text-xs text-text-muted truncate flex-1">
          {row.timeRange}
        </span>
        {row.status ? (
          <StatusBadge
            status={row.status as AppointmentStatusType}
            label={row.statusDisplay}
            className="text-xs px-1 py-px"
          />
        ) : null}
      </div>
    </button>
  );
}

function defaultHours(): number[] {
  return Array.from({ length: 10 }, (_, i) => i + 8); // 8..17
}

// ─── grouping helper ──────────────────────────────────────────────────

/**
 * Bucket day-list rows by `practitionerId`. Doctor columns without any
 * appointments still render (header strip + empty grid) — the caller's
 * doctors array drives column visibility.
 *
 * Rows with no `practitionerId` are DROPPED — they have no column to
 * live in. Drift in the mapper (or backend) is logged via console.warn
 * in dev so it's caught early; never silently rolled into a catch-all
 * "undefined" column.
 *
 * Duplicate practitionerIds are aggregated under the same bucket and
 * sorted by start time — multiple appointments per doctor is the
 * expected case.
 */
export function groupRowsByDoctor(
  rows: DayListRow[],
): Map<string, DayListRow[]> {
  const out = new Map<string, DayListRow[]>();
  let droppedNoId = 0;
  for (const row of rows) {
    const id = row.practitionerId;
    if (!id) {
      droppedNoId++;
      continue;
    }
    const list = out.get(id) ?? [];
    list.push(row);
    out.set(id, list);
  }
  if (droppedNoId > 0 && import.meta.env.DEV) {
    // Surface the drift loudly during development — production traffic
    // never hits this branch on well-formed mapper output, so the log
    // signals a regression in calendar-mapping or backend payload.
    // eslint-disable-next-line no-console
    console.warn(
      `[ByDoctorCalendar] groupRowsByDoctor dropped ${droppedNoId} row(s) with no practitionerId — check the mapper.`,
    );
  }
  // Sort each list by start time for stable rendering.
  for (const list of out.values()) {
    list.sort((a, b) => a.startISO.localeCompare(b.startISO));
  }
  return out;
}
